<template>
  <AdminLayout>
    <template #content>
      <div class="d-flex justify-content-between align-items-start gap-3 mb-3">
        <div>
          <h1 class="h4 mb-1">
            Détail du transfert
            <span v-if="transfer" class="text-muted">#{{ transfer.reference }}</span>
          </h1>
          <div v-if="transfer" class="small text-muted">
            {{ sourceShopName }} vers {{ destinationShopName }}
            <span class="badge ms-2" :class="statusClass(transfer.status)">
              {{ statusLabel(transfer.status) }}
            </span>
          </div>
        </div>

        <div class="d-flex flex-wrap justify-content-end gap-2">
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm btn-icon-label"
            title="Retour à la liste"
            @click="$router.push({ name: 'StockTransfers' })"
          >
            <i class="fa fa-arrow-left"></i>
            <span class="btn-label ms-1">Retour à la liste</span>
          </button>

          <button
            v-if="canEditTransfer"
            type="button"
            class="btn btn-outline-primary btn-sm btn-icon-label"
            title="Modifier"
            @click="$router.push({ name: 'StockTransferEdit', params: { id: transfer.id } })"
          >
            <i class="fa fa-pen"></i>
            <span class="btn-label ms-1">Modifier</span>
          </button>

          <button
            v-if="canSendSelected"
            type="button"
            class="btn btn-success btn-sm btn-icon-label"
            title="Expédier"
            @click="sendTransfer"
          >
            <i class="fa fa-paper-plane"></i>
            <span class="btn-label ms-1">Expédier</span>
          </button>

          <button
            v-if="canReceiveSelected"
            type="button"
            class="btn btn-primary btn-sm btn-icon-label"
            :disabled="isReceiving || !canSubmitReceive"
            title="Réceptionner"
            @click="receiveTransfer"
          >
            <i class="fa" :class="isReceiving ? 'fa-spinner fa-spin' : 'fa-box-open'"></i>
            <span class="btn-label ms-1">{{ isReceiving ? 'Réception...' : 'Réceptionner' }}</span>
          </button>

          <button
            v-if="canCancelSelected"
            type="button"
            class="btn btn-outline-danger btn-sm btn-icon-label"
            title="Annuler"
            @click="cancelTransfer"
          >
            <i class="fa fa-ban"></i>
            <span class="btn-label ms-1">Annuler</span>
          </button>

          <AppLoadingButton
            v-if="canDeleteSelected"
            type="button"
            class="btn btn-outline-danger btn-sm btn-icon-label"
            :loading="isDeleting"
            loading-label="Suppression..."
            title="Supprimer"
            @click="deleteTransfer"
          >
            <i class="fa fa-trash"></i>
            <span class="btn-label ms-1">Supprimer</span>
          </AppLoadingButton>
        </div>
      </div>

      <div v-if="successMessage" class="alert alert-success py-2">
        {{ successMessage }}
      </div>

      <div v-if="isLoading" class="text-center my-5">
        <i class="fa fa-spinner fa-spin fa-2x mb-2"></i>
        <div class="small text-muted">Chargement du transfert...</div>
      </div>

      <div v-else-if="errorMessage" class="alert alert-danger">
        {{ errorMessage }}
      </div>

      <div v-else-if="!transfer" class="alert alert-warning">
        Transfert introuvable.
      </div>

      <div v-else class="row g-3 align-items-start">
        <div class="col-lg-4">
          <div class="card border-0 shadow-sm">
            <div class="card-header bg-white border-0 pb-1">
              <h6 class="mb-0">Informations et totaux</h6>
            </div>
            <div class="card-body pt-2 pb-3">
              <div class="detail-row">
                <span>Référence</span>
                <strong>{{ transfer.reference }}</strong>
              </div>
              <div class="detail-row">
                <span>Statut</span>
                <span class="badge" :class="statusClass(transfer.status)">
                  {{ statusLabel(transfer.status) }}
                </span>
              </div>
              <div class="detail-row">
                <span>Source</span>
                <strong>{{ sourceShopName }}</strong>
              </div>
              <div class="detail-row">
                <span>Destination</span>
                <strong>{{ destinationShopName }}</strong>
              </div>
              <div class="detail-row">
                <span>Créé le</span>
                <strong>{{ $format.datetime(transfer.created_at) }}</strong>
              </div>
              <div class="detail-row">
                <span>Créé par</span>
                <strong>{{ transfer.creator?.name || transfer.creator?.username || '-' }}</strong>
              </div>
              <div v-if="transfer.sent_at" class="detail-row">
                <span>Expédié le</span>
                <strong>{{ $format.datetime(transfer.sent_at) }}</strong>
              </div>
              <div v-if="transfer.received_at" class="detail-row">
                <span>Réceptionné le</span>
                <strong>{{ $format.datetime(transfer.received_at) }}</strong>
              </div>
              <div v-if="transfer.cancelled_at" class="detail-row">
                <span>Annulé le</span>
                <strong>{{ $format.datetime(transfer.cancelled_at) }}</strong>
              </div>

              <hr class="my-3" />

              <div class="detail-row">
                <span>Lignes</span>
                <strong>{{ transferItems.length }}</strong>
              </div>
              <div class="detail-row">
                <span>Quantité totale</span>
                <strong>{{ totalQuantity }}</strong>
              </div>
              <div class="detail-row">
                <span>Coût estimé</span>
                <strong>{{ $format.currency(totalCost) }}</strong>
              </div>

              <div v-if="transfer.notes" class="mt-3">
                <div class="small text-muted">Notes</div>
                <div>{{ transfer.notes }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-lg-8">
          <div v-if="canReceiveSelected" class="card border-0 shadow-sm mb-3 receive-catalog-card">
            <div class="card-header bg-white border-0 pb-1">
              <div class="d-flex flex-wrap justify-content-between align-items-start gap-2">
                <div>
                  <h6 class="mb-1">Rangement de réception</h6>
                  <small class="text-muted">
                    Sélectionnez le rayon et la catégorie destination de chaque produit avant de réceptionner.
                  </small>
                </div>

                <div class="receive-bulk-actions">
                  <div class="receive-bulk-select">
                    <label class="form-label small fw-semibold mb-1" for="receiveRayonAll">
                      Rayon pour toutes les lignes
                    </label>
                    <select
                      id="receiveRayonAll"
                      v-model="bulkReceiveRayonId"
                      class="form-select form-select-sm"
                      :disabled="isReceiving || isRayonsLoading || !rayons.length"
                      @change="applyReceiveRayonToAll"
                    >
                      <option value="">Choisir...</option>
                      <option v-for="rayon in rayons" :key="rayon.id" :value="String(rayon.id)">
                        {{ rayon.name }}
                      </option>
                    </select>
                  </div>

                  <div class="receive-bulk-select">
                    <label class="form-label small fw-semibold mb-1" for="receiveCategoryAll">
                      Catégorie pour toutes les lignes
                    </label>
                    <select
                      id="receiveCategoryAll"
                      v-model="bulkReceiveCategoryId"
                      class="form-select form-select-sm"
                      :disabled="isReceiving || isCategoriesLoading || !categories.length"
                      @change="applyReceiveCategoryToAll"
                    >
                      <option value="">Choisir...</option>
                      <option v-for="category in categories" :key="category.id" :value="String(category.id)">
                        {{ categoryLabel(category) }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div class="card-body pt-2">
              <div v-if="receiveErrorMessage" class="alert alert-danger py-2 mb-3">
                {{ receiveErrorMessage }}
              </div>

              <div v-if="isRayonsLoading || isCategoriesLoading" class="text-center py-3">
                <i class="fa fa-spinner fa-spin me-1"></i>
                <span class="small text-muted">Chargement des rayons et catégories...</span>
              </div>

              <div v-else-if="!rayons.length || !categories.length" class="alert alert-warning py-2 mb-0">
                <div v-if="!rayons.length">Aucun rayon actif disponible pour ce site.</div>
                <div v-if="!categories.length">Aucune catégorie active disponible pour ce site.</div>
              </div>

              <div v-else class="receive-rayon-list">
                <div
                  v-for="item in transferItems"
                  :key="item.id"
                  class="receive-rayon-row"
                >
                  <div class="receive-rayon-product">
                    <div class="fw-semibold">{{ item.product_name }}</div>
                    <small class="text-muted">
                      {{ item.product_sku || 'Sans SKU' }} · Qté {{ item.quantity }}
                    </small>
                  </div>

                  <div class="receive-rayon-control">
                    <label class="form-label small fw-semibold mb-1" :for="`receiveRayon-${item.id}`">
                      Rayon destination
                    </label>
                    <select
                      :id="`receiveRayon-${item.id}`"
                      v-model="selectedReceiveRayonIds[item.id]"
                      class="form-select form-select-sm receive-rayon-select"
                      :disabled="isReceiving"
                    >
                      <option value="">Choisir un rayon</option>
                      <option v-for="rayon in rayons" :key="rayon.id" :value="String(rayon.id)">
                        {{ rayon.name }}
                      </option>
                    </select>
                  </div>

                  <div class="receive-category-control">
                    <label class="form-label small fw-semibold mb-1" :for="`receiveCategory-${item.id}`">
                      Catégorie destination
                    </label>
                    <select
                      :id="`receiveCategory-${item.id}`"
                      v-model="selectedReceiveCategoryIds[item.id]"
                      class="form-select form-select-sm receive-category-select"
                      :disabled="isReceiving"
                    >
                      <option value="">Choisir une catégorie</option>
                      <option v-for="category in categories" :key="category.id" :value="String(category.id)">
                        {{ categoryLabel(category) }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="d-flex justify-content-between align-items-center mb-2">
            <h6 class="mb-0">Produits du transfert</h6>
            <small v-if="transferItems.length" class="text-muted">
              {{ transferItems.length }} ligne(s)
            </small>
          </div>

          <BootstrapDataTable
            :rows="transferItems"
            :columns="lineColumns"
            row-key="id"
            empty-text="Aucune ligne de transfert."
            client-pagination
            :per-page="10"
            :show-refresh="false"
            :show-export="false"
            :show-print="false"
          >
            <template #cell-product_name="{ row }">
              <div class="fw-semibold">{{ row.product_name }}</div>
            </template>
            <template #cell-product_sku="{ row }">
              {{ row.product_sku || '-' }}
            </template>
            <template #cell-unit_cost="{ row }">
              {{ $format.currency(row.unit_cost || 0) }}
            </template>
            <template #cell-line_total="{ row }">
              {{ $format.currency(row.line_total || 0) }}
            </template>
          </BootstrapDataTable>
        </div>
      </div>
    </template>
  </AdminLayout>
</template>

<script>
export default {
  name: 'StockTransferShowView',
  data() {
    return {
      transfer: null,
      isLoading: false,
      errorMessage: '',
      successMessage: '',
      isDeleting: false,
      isReceiving: false,
      isRayonsLoading: false,
      isCategoriesLoading: false,
      receiveErrorMessage: '',
      rayons: [],
      categories: [],
      selectedReceiveRayonIds: {},
      selectedReceiveCategoryIds: {},
      bulkReceiveRayonId: '',
      bulkReceiveCategoryId: '',
      lineColumns: [
        { key: 'product_name', label: 'Produit' },
        { key: 'product_sku', label: 'SKU source' },
        { key: 'quantity', label: 'Quantité', class: 'text-end', headerClass: 'text-end' },
        { key: 'unit_cost', label: 'Coût unitaire', class: 'text-end', headerClass: 'text-end' },
        { key: 'line_total', label: 'Total ligne', class: 'text-end', headerClass: 'text-end' },
      ],
    };
  },
  computed: {
    currentShopId() {
      return this.$store.getters['shop/currentShopId'];
    },
    transferItems() {
      return (this.transfer?.items || []).map((item) => ({
        ...item,
        line_total: Number(item.quantity || 0) * Number(item.unit_cost || 0),
      }));
    },
    totalQuantity() {
      return this.transferItems.reduce((total, item) => total + Number(item.quantity || 0), 0);
    },
    totalCost() {
      return this.transferItems.reduce((total, item) => total + Number(item.line_total || 0), 0);
    },
    sourceShopName() {
      return this.transfer?.source_shop?.name || this.transfer?.sourceShop?.name || '-';
    },
    destinationShopName() {
      return this.transfer?.destination_shop?.name || this.transfer?.destinationShop?.name || '-';
    },
    canEditTransfer() {
      return this.transfer?.status === 'draft'
        && Number(this.transfer?.source_shop_id) === Number(this.currentShopId);
    },
    canSendSelected() {
      return this.canEditTransfer;
    },
    canReceiveSelected() {
      return this.transfer?.status === 'sent'
        && Number(this.transfer?.destination_shop_id) === Number(this.currentShopId);
    },
    canSubmitReceive() {
      return this.canReceiveSelected
        && !this.isRayonsLoading
        && !this.isCategoriesLoading
        && this.rayons.length > 0
        && this.categories.length > 0
        && this.transferItems.every((item) => (
          this.selectedReceiveRayonIds[item.id]
          && this.selectedReceiveCategoryIds[item.id]
        ));
    },
    canCancelSelected() {
      return this.transfer?.status === 'sent'
        && Number(this.transfer?.source_shop_id) === Number(this.currentShopId);
    },
    canDeleteSelected() {
      return this.canEditTransfer;
    },
  },
  mounted() {
    this.fetchTransfer();
  },
  methods: {
    async fetchTransfer() {
      this.isLoading = true;
      this.errorMessage = '';
      this.receiveErrorMessage = '';
      this.transfer = null;

      try {
        const response = await this.$api.get(`/stock-transfers/${this.$route.params.id}`);
        this.transfer = response.data.data;
        this.initializeReceiveRayonSelection();
        this.initializeReceiveCategorySelection();

        if (this.canReceiveSelected) {
          await Promise.all([
            this.fetchRayons(),
            this.fetchCategories(),
          ]);
        }
      } catch (error) {
        this.errorMessage = 'Impossible de charger le transfert.';
      } finally {
        this.isLoading = false;
      }
    },
    async fetchRayons() {
      this.isRayonsLoading = true;

      try {
        const response = await this.$api.get('/rayons', {
          params: { only_active: true },
        });
        this.rayons = response.data?.data || [];
        this.initializeReceiveRayonSelection();
      } catch (error) {
        console.error('Erreur chargement rayons', error);
        this.rayons = [];
        this.receiveErrorMessage = 'Impossible de charger les rayons de réception.';
      } finally {
        this.isRayonsLoading = false;
      }
    },
    async fetchCategories() {
      this.isCategoriesLoading = true;

      try {
        const response = await this.$api.get('/categories', {
          params: { only_active: true },
        });
        this.categories = response.data?.data || [];
        this.initializeReceiveCategorySelection();
      } catch (error) {
        console.error('Erreur chargement catégories', error);
        this.categories = [];
        this.receiveErrorMessage = 'Impossible de charger les catégories de réception.';
      } finally {
        this.isCategoriesLoading = false;
      }
    },
    initializeReceiveRayonSelection() {
      const nextSelection = {};

      this.transferItems.forEach((item) => {
        nextSelection[item.id] = this.selectedReceiveRayonIds[item.id] || '';
      });

      if (this.rayons.length === 1) {
        this.transferItems.forEach((item) => {
          nextSelection[item.id] = String(this.rayons[0].id);
        });
        this.bulkReceiveRayonId = String(this.rayons[0].id);
      }

      this.selectedReceiveRayonIds = nextSelection;
    },
    initializeReceiveCategorySelection() {
      const nextSelection = {};

      this.transferItems.forEach((item) => {
        const currentCategoryId = this.selectedReceiveCategoryIds[item.id];
        const suggestedCategory = this.findSuggestedCategoryForItem(item);

        nextSelection[item.id] = currentCategoryId || (suggestedCategory ? String(suggestedCategory.id) : '');
      });

      const selectedValues = Object.values(nextSelection).filter(Boolean);
      const uniqueValues = [...new Set(selectedValues)];

      this.bulkReceiveCategoryId = selectedValues.length === this.transferItems.length && uniqueValues.length === 1
        ? uniqueValues[0]
        : '';
      this.selectedReceiveCategoryIds = nextSelection;
    },
    applyReceiveRayonToAll() {
      const rayonId = this.bulkReceiveRayonId || '';
      const nextSelection = {};

      this.transferItems.forEach((item) => {
        nextSelection[item.id] = rayonId;
      });

      this.selectedReceiveRayonIds = nextSelection;
    },
    applyReceiveCategoryToAll() {
      const categoryId = this.bulkReceiveCategoryId || '';
      const nextSelection = {};

      this.transferItems.forEach((item) => {
        nextSelection[item.id] = categoryId;
      });

      this.selectedReceiveCategoryIds = nextSelection;
    },
    findSuggestedCategoryForItem(item) {
      if (!this.categories.length) return null;

      const sourceProduct = item.source_product || item.sourceProduct || {};
      const sourceCategory = sourceProduct.category || {};
      const sourceCategoryCode = this.normalizedCatalogValue(sourceCategory.code);

      if (sourceCategoryCode) {
        const categoryByCode = this.categories.find((category) => (
          this.normalizedCatalogValue(category.code) === sourceCategoryCode
        ));

        if (categoryByCode) return categoryByCode;
      }

      const sourceCategoryName = this.normalizedCatalogValue(
        sourceCategory.name || sourceProduct.categorie || item.categorie || item.category_name
      );

      if (sourceCategoryName) {
        const categoryByName = this.categories.find((category) => (
          this.normalizedCatalogValue(category.name) === sourceCategoryName
        ));

        if (categoryByName) return categoryByName;
      }

      return this.categories.find((category) => (
        category.type === 'system' && category.system_key === 'default'
      )) || (this.categories.length === 1 ? this.categories[0] : null);
    },
    normalizedCatalogValue(value) {
      return String(value || '').trim().toLocaleLowerCase();
    },
    categoryLabel(category) {
      return category.code ? `${category.code} - ${category.name}` : category.name;
    },
    async sendTransfer() {
      if (!this.transfer) return;
      if (!(await this.$confirmAction(`Expédier le transfert ${this.transfer.reference} ?`))) return;

      try {
        await this.$api.post(`/stock-transfers/${this.transfer.id}/send`);
        this.successMessage = 'Transfert expédié.';
        await this.fetchTransfer();
      } catch (error) {
        this.errorMessage = this.extractErrorMessage(error, 'Impossible d’expédier le transfert.');
      }
    },
    async receiveTransfer() {
      if (!this.transfer) return;
      if (!this.canSubmitReceive) {
        if (!this.rayons.length) {
          this.receiveErrorMessage = 'Aucun rayon actif disponible pour réceptionner ce transfert.';
        } else if (!this.categories.length) {
          this.receiveErrorMessage = 'Aucune catégorie active disponible pour réceptionner ce transfert.';
        } else {
          this.receiveErrorMessage = 'Sélectionnez un rayon et une catégorie pour chaque ligne à réceptionner.';
        }

        return;
      }

      if (!(await this.$confirmAction(`Réceptionner le transfert ${this.transfer.reference} ?`))) return;

      this.isReceiving = true;
      this.receiveErrorMessage = '';

      try {
        await this.$api.post(`/stock-transfers/${this.transfer.id}/receive`, {
          items: this.transferItems.map((item) => ({
            id: item.id,
            rayon_id: Number(this.selectedReceiveRayonIds[item.id]),
            category_id: Number(this.selectedReceiveCategoryIds[item.id]),
          })),
        });
        this.successMessage = 'Transfert réceptionné.';
        await this.fetchTransfer();
      } catch (error) {
        this.receiveErrorMessage = this.extractErrorMessage(error, 'Impossible de réceptionner le transfert.');
      } finally {
        this.isReceiving = false;
      }
    },
    async cancelTransfer() {
      if (!this.transfer) return;
      if (!(await this.$confirmAction(`Annuler le transfert expédié ${this.transfer.reference} ? Le stock source sera restauré.`))) return;

      try {
        await this.$api.post(`/stock-transfers/${this.transfer.id}/cancel`);
        this.successMessage = 'Transfert annulé.';
        await this.fetchTransfer();
      } catch (error) {
        this.errorMessage = this.extractErrorMessage(error, 'Impossible d’annuler le transfert.');
      }
    },
    async deleteTransfer() {
      if (!this.transfer || !this.canDeleteSelected) return;
      if (this.isDeleting) return;

      this.isDeleting = true;

      if (!(await this.$confirmAction(`Supprimer le transfert ${this.transfer.reference} ?`))) {
        this.isDeleting = false;
        return;
      }

      try {
        await this.$api.delete(`/stock-transfers/${this.transfer.id}`);
        this.$router.push({ name: 'StockTransfers' });
      } catch (error) {
        this.errorMessage = this.extractErrorMessage(error, 'Impossible de supprimer le transfert.');
      } finally {
        this.isDeleting = false;
      }
    },
    extractErrorMessage(error, fallback) {
      if (error?.response?.data?.errors) {
        return Object.values(error.response.data.errors).flat().join(' ');
      }

      return fallback;
    },
    statusLabel(status) {
      return {
        draft: 'Brouillon',
        sent: 'Expédié',
        received: 'Réceptionné',
        cancelled: 'Annulé',
      }[status] || status;
    },
    statusClass(status) {
      return {
        draft: 'bg-secondary',
        sent: 'bg-warning text-dark',
        received: 'bg-success',
        cancelled: 'bg-danger',
      }[status] || 'bg-secondary';
    },
  },
};
</script>

<style scoped>
.detail-row {
  align-items: flex-start;
  display: flex;
  gap: 0.75rem;
  justify-content: space-between;
  margin-bottom: 0.55rem;
}

.detail-row span:first-child {
  color: #6c757d;
  font-size: 0.82rem;
}

.detail-row strong {
  text-align: right;
}

.receive-catalog-card {
  border-left: 4px solid var(--bs-primary) !important;
}

.receive-bulk-actions {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(2, minmax(210px, 1fr));
}

.receive-bulk-select {
  min-width: 220px;
}

.receive-rayon-list {
  display: grid;
  gap: 0.75rem;
}

.receive-rayon-row {
  align-items: end;
  border: 1px solid rgba(33, 37, 41, 0.08);
  border-radius: 0.5rem;
  display: grid;
  gap: 0.75rem;
  grid-template-columns: minmax(0, 1fr) minmax(190px, 240px) minmax(190px, 240px);
  padding: 0.75rem;
}

.receive-rayon-product {
  min-width: 0;
}

@media (max-width: 767.98px) {
  .receive-bulk-actions {
    grid-template-columns: 1fr;
    width: 100%;
  }

  .receive-bulk-select,
  .receive-rayon-control,
  .receive-category-control {
    width: 100%;
  }

  .receive-rayon-row {
    grid-template-columns: 1fr;
  }
}
</style>
